import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ActivityFeed from "@/components/dashboard/ActivityFeed";
import DashboardStats from "@/components/dashboard/DashboardStats";
import ProjectAnalytics from "@/components/dashboard/ProjectAnalytics";
import { useJobMatching } from "@/components/job-matching/useJobMatching";
import { useToast } from "@/components/ui/use-toast";
import { FreelancerDetailModal } from "@/components/auth/FreelancerDetailModal";
import EnterpriseDetailModal from "@/components/auth/EnterpriseDetailModal";
import { useUserStore } from "@/store/userStore";
import { useFreelancerStore } from "@/store/freelancerStore";
import AILoader from "@/components/common/AILoader";
import LoaderOverlay from "@/components/common/LoaderOverlay";
import CircularDotsLoader from "@/components/common/CircularLoader";
import { useEnterpriseStore } from "@/store/enterpriseStore";

const Dashboard = () => {
  const { setJobAlert } = useJobMatching();
  const { toast } = useToast();
  const { user } = useUserStore();
   const { isLoading: loader,fetchFreelancerProfile } = useFreelancerStore();
  const [isModalFreelancerOpen, setIsModalFreelancerOpen] = useState(false);
  const [isModalEnterpriseOpen, setIsModalEnterpriseOpen] = useState(false);
  const [isAIDetails, setIsAIDetails] = useState(true)
  const [userDataResponse, setUserDataResponse] = useState<any>(null);

  const { fetchEnterpriseProfile } = useEnterpriseStore();

  const handleCloseModal = () => {
    setIsModalFreelancerOpen(!isModalFreelancerOpen);
  };
  const handleEnterpriseCloseModal = () => {
    setIsModalEnterpriseOpen(!isModalEnterpriseOpen);
  };

  const handleJobAlert = () => {
    setJobAlert("Senior React Developer");
    toast({
      title: "Job Alert Set",
      description:
        "You'll receive notifications for Senior React Developer positions",
    });
  };

   useEffect(() => {
    if (user) {
      if (user.role === "freelancer") {
        fetchFreelancerProfile(user.userId, user.accessToken);
      } else {
        fetchEnterpriseProfile(user.userId, user.accessToken);
      }
    }
  }, []);

  useEffect(() => {
    if (
      localStorage.getItem("showFreelancerModal") === "true" ||
      (user && user?.role === "freelancer" && user?.isDetailsSaved === false)
    ) {
      const result =
        JSON.parse(localStorage.getItem("userDataResponse")) || null;
      setUserDataResponse(result);
      setTimeout(()=>{
        handleCloseModal();
      },1000)
      localStorage.removeItem("showFreelancerModal");
    }

    if (
      localStorage.getItem("showEnterpriseModal") === "true" ||
      (user && user?.role === "Enterprise" && user?.isDetailsSaved === false)
    ) {
      const result =
        JSON.parse(localStorage.getItem("userDataResponse")) || null;
      setUserDataResponse(result);
      setTimeout(()=>{
        handleEnterpriseCloseModal();
      },1000)
      localStorage.removeItem("showEnterpriseModal");
    }
  }, []);

  return (
    <>
    {loader && <CircularDotsLoader text="AI is scanning your resume to auto-fill information..." />}
    <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto animate-fade-in">
        <h1 className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">
          Dashboard
        </h1>
        <p className="text-gray-600 mb-6">
          Welcome to your professional dashboard
        </p>

        {/* Dashboard Stats Section */}
        <DashboardStats />

        {/* Project Analytics Section */}
        <ProjectAnalytics />

        {/* Dashboard Actions and Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
                <CardDescription>
                  Set alerts and manage your preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={handleJobAlert}
                    variant="default"
                    className="bg-gradient-to-r from-blue-600 to-indigo-600"
                  >
                    Set Job Alert
                  </Button>
                  <Button variant="outline">Update Profile</Button>
                  <Button variant="outline">View Recent Proposals</Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Projects</CardTitle>
                <CardDescription>
                  Your latest ongoing and completed projects
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="ongoing" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4">
                    <TabsTrigger value="ongoing">Ongoing</TabsTrigger>
                    <TabsTrigger value="completed">Completed</TabsTrigger>
                  </TabsList>
                  <TabsContent value="ongoing" className="space-y-4">
                    {[1, 2, 3].map((project) => (
                      <div
                        key={project}
                        className="flex justify-between items-center p-4 border rounded-lg"
                      >
                        <div>
                          <h3 className="font-medium">Project {project}</h3>
                          <p className="text-sm text-gray-500">
                            Client: Company {project}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </div>
                    ))}
                  </TabsContent>
                  <TabsContent value="completed" className="space-y-4">
                    {[4, 5, 6].map((project) => (
                      <div
                        key={project}
                        className="flex justify-between items-center p-4 border rounded-lg"
                      >
                        <div>
                          <h3 className="font-medium">Project {project}</h3>
                          <p className="text-sm text-gray-500">
                            Client: Company {project}
                          </p>
                        </div>
                        <Button variant="outline" size="sm">
                          View
                        </Button>
                      </div>
                    ))}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Activity Feed */}
          <ActivityFeed />
        </div>
      </div>
      <FreelancerDetailModal
        isOpen={isModalFreelancerOpen}
        onClose={handleCloseModal}
        setIsAIDetails={setIsAIDetails}
        initialData={{
          userId: userDataResponse?.userId || "",
          name: userDataResponse?.name || "",
          email: userDataResponse?.email || "",
          accessToken: userDataResponse?.accessToken || "",
          role: userDataResponse?.role || "",
          phone: "",
          location: "",
          bio: "",
          designation: "",
          category: "",
          experience: 0,
          projectsCompleted: 0,
          dailyRate: 0,
          skills: [],
          certifications: [],
          resume_url: null,
          achievements: [],
        }}
      />
      <EnterpriseDetailModal
        isOpen={isModalEnterpriseOpen}
        onClose={handleEnterpriseCloseModal}
        initialData={{
          EnterpriseId: userDataResponse?.userId || "",
          name: userDataResponse?.name || "",
          email: userDataResponse?.email || "",
          accessToken: userDataResponse?.accessToken || "",
          role: userDataResponse?.role || "",
          companyName: "",
          industryType: "",
          contactPerson: { name: "", email: "", phone: "" },
          location: { address: "", city: "", country: "" },
          preferredCurrency: "USD",
          preferredPaymentMethod: "",
          vatOrGstId: "",
          socialMediaProfile: "",
          previousProjects: [],
        }}
      />
    </>
  );
};

export default Dashboard;
